import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Scale, Calculator, BookOpen, Network } from "lucide-react";
import { prodApi, fmtKg, fmtPct, fmtEur, fmtDate, errMsg } from "./prodApi";
import { useProd } from "./ProdApp";
import { PButton, PCard, PTitle, KPI, PEmpty, PBadge } from "./ProdUi";

export default function ProdDashboard() {
  const { user, empty, recipes, reload } = useProd();
  const [data, setData] = useState(null);
  const navigate = useNavigate();

  useEffect(() => { prodApi.get("/production/dashboard").then((r) => setData(r.data)).catch(() => {}); }, []);

  const seed = async () => {
    try {
      await prodApi.post("/production/seed-demo");
      await reload();
      toast.success("Albero bovino d'esempio caricato");
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="space-y-6" data-testid="prod-dashboard">
      <PTitle kicker="Produzione" title={`Ciao ${user?.name?.split(" ")[0] || ""}`}>Rese, costi e marginalità delle lavorazioni</PTitle>

      {empty && (
        <PEmpty title="L'albero dei tagli è vuoto" text="Carica l'esempio bovino (carcassa → mezzena → pistola → lombata/coscia → tagli, con rese e coefficienti) oppure crea i tagli a mano dalla sezione Albero." testId="prod-empty-tree"
          action={user?.is_manager ? <PButton variant="primary" onClick={seed} data-testid="prod-seed-btn">Carica esempio bovino</PButton> : <span className="text-slate-500 text-sm">Chiedi al responsabile di caricare la configurazione</span>} />
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <KPI label="Kg lavorati" value={fmtKg(data?.input_kg, 0)} sub={`${data?.runs_count ?? 0} lavorazioni reali`} testId="prod-kpi-kg-lavorati" />
        <KPI label="Resa vendibile media" value={fmtPct(data?.yield_pct)} sub={`Scarto ${fmtPct(data?.waste_pct)}`} tone="green" testId="prod-kpi-resa-vendibile" />
        <KPI label="Margine reale" value={fmtEur(data?.margin)} sub={fmtPct(data?.margin_pct) + " sul fatturato"} tone={data?.margin < 0 ? "red" : "blue"} testId="prod-kpi-margine-medio" />
        <KPI label="Ricette attive" value={data?.recipes_count ?? recipes.length} sub={`${data?.nodes_count ?? 0} tagli in albero`} testId="prod-kpi-ricette" />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          ["Resa reale rapida", "Registra una lavorazione in reparto", Scale, "/produzione/resa-rapida", "success", "prod-quick-action-resa"],
          ["Calcolatore", "Simula una lavorazione e i margini", Calculator, "/produzione/calcolatore", "primary", "prod-quick-action-calc"],
          ["Ricette", "Rese teoriche e costi", BookOpen, "/produzione/ricette", "ghost", "prod-quick-action-ricette"],
          ["Albero tagli", "Gerarchia e coefficienti", Network, "/produzione/albero", "ghost", "prod-quick-action-albero"],
        ].map(([t, s, Icon, to, v, tid]) => (
          <PButton key={to} variant={v} className="!h-auto py-5 flex-col items-start text-left" onClick={() => navigate(to)} data-testid={tid}>
            <Icon className="w-7 h-7 mb-2" />
            <span className="text-base">{t}</span>
            <span className="text-xs font-medium opacity-70">{s}</span>
          </PButton>
        ))}
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <PCard>
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-['Chivo'] font-black text-lg text-white">Ultime lavorazioni</h2>
            <Link to="/produzione/lavorazioni" className="text-sm text-blue-400 font-bold">Tutte →</Link>
          </div>
          {!data?.last_runs?.length && <p className="text-slate-500 text-sm">Nessuna lavorazione registrata. Avvia una "Resa reale rapida".</p>}
          <div className="divide-y divide-slate-700/60">
            {data?.last_runs?.map((r) => (
              <Link to={`/produzione/lavorazioni/${r.id}`} key={r.id} className="flex items-center justify-between py-3 gap-3 hover:bg-slate-800/60 -mx-2 px-2 rounded-lg" data-testid={`prod-dash-run-${r.number}`}>
                <div className="min-w-0">
                  <p className="font-semibold text-white truncate">{r.recipe_name}</p>
                  <p className="text-xs text-slate-400">{r.number} · {fmtDate(r.date)} · {r.operator}</p>
                </div>
                <div className="text-right shrink-0">
                  <p className="font-['Chivo'] font-bold tabular-nums">{fmtKg(r.result?.mass?.input_kg, 1)} kg</p>
                  <PBadge tone={r.result?.comparison?.diff_sellable_kg >= 0 ? "green" : "amber"}>resa {fmtPct(r.result?.mass?.sellable_yield_pct)}</PBadge>
                </div>
              </Link>
            ))}
          </div>
        </PCard>
        <PCard>
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-['Chivo'] font-black text-lg text-white">Ultime simulazioni</h2>
            <Link to="/produzione/simulazioni" className="text-sm text-blue-400 font-bold">Tutte →</Link>
          </div>
          {!data?.last_simulations?.length && <p className="text-slate-500 text-sm">Nessuna simulazione salvata.</p>}
          <div className="divide-y divide-slate-700/60">
            {data?.last_simulations?.map((s) => (
              <Link to={`/produzione/simulazioni/${s.id}`} key={s.id} className="flex items-center justify-between py-3 gap-3 hover:bg-slate-800/60 -mx-2 px-2 rounded-lg" data-testid={`prod-dash-sim-${s.number}`}>
                <div className="min-w-0">
                  <p className="font-semibold text-white truncate">{s.name}</p>
                  <p className="text-xs text-slate-400">{s.number} · {s.recipe_name} · {fmtDate(s.created_at)}</p>
                </div>
                <div className="text-right shrink-0">
                  <p className={`font-['Chivo'] font-bold tabular-nums ${s.result?.result?.margin < 0 ? "text-red-400" : "text-emerald-400"}`}>{fmtEur(s.result?.result?.margin)}</p>
                  <p className="text-xs text-slate-400">{fmtPct(s.result?.result?.margin_pct)}</p>
                </div>
              </Link>
            ))}
          </div>
        </PCard>
      </div>
    </div>
  );
}
